import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { GLOSSARY } from "./Glossary";

/**
 * Contextual help content for each page
 */
const PAGE_HELP: Record<
 string,
 {
 title: string;
 description: string;
 tips: string[];
 relatedTerms?: string[];
 }
> = {"/dashboard": {
 title: "Dashboard",
 description: "Your personalized overview showing key metrics, recent activity, and quick actions.",
 tips: ["Check your daily stats to see hours logged today","Use quick actions to start tracking time or submit timesheets","Review pending approvals if you're a manager",
 ],
 relatedTerms: ["timesheet","worklog","allocation"],
 },"/projects": {
 title: "Projects",
 description: "View and manage all projects you have access to. Track progress and milestones.",
 tips: ["Click on a project to see detailed information","Use filters to find specific projects by status","Check the progress bar for overall completion",
 ],
 relatedTerms: ["milestone","RACI","project manager"],
 },"/my-tasks": {
 title: "My Tasks",
 description: "View all tasks assigned to you across different projects.",
 tips: ["Update task status as you make progress","Click on a task to see full details and log time","Use priority indicators to focus on important work",
 ],
 relatedTerms: ["assignee","estimated hours","priority"],
 },"/timesheets": {
 title: "Timesheets",
 description: "Weekly summary of your work hours. Submit for approval when complete.",
 tips: ["Fill in hours for each day of the week","Submit your timesheet before the deadline","Review rejected timesheets and resubmit",
 ],
 relatedTerms: ["timesheet","approved","submitted"],
 },"/worklogs": {
 title: "Work Logs",
 description: "Detailed record of time spent on individual tasks.",
 tips: ["Log time as you complete work for accuracy","Add notes to describe what you worked on","Edit logs before they're approved",
 ],
 relatedTerms: ["worklog","billable","actual hours"],
 },"/leaves": {
 title: "Leaves",
 description: "Request time off and view your leave balance.",
 tips: ["Check your remaining balance before requesting","Submit leave requests in advance when possible","Track the status of your requests",
 ],
 },"/users": {
 title: "Users",
 description: "Manage team members and their roles (Admin only).",
 tips: ["Click on a user to see their full profile","Update roles to change permissions","View workload to check availability",
 ],
 relatedTerms: ["team lead","project manager"],
 },"/settings": {
 title: "Settings",
 description: "Configure your account preferences and organization settings.",
 tips: ["Update your notification preferences","Review your profile information","Contact admin for permission changes",
 ],
 },
};

const DEFAULT_HELP = {
 title: "Help",
 description: "Welcome to the Time Tracking application. Use the tips below to get started.",
 tips: ["Press Cmd+K (or Ctrl+K) to open quick search","Click the help icon (?) on any page for contextual help","Hover over underlined terms to see definitions",
 ],
};

/**
 * Floating Help Button - Appears in bottom right corner
 */
export function HelpButton({ onClick }: { onClick: () => void }) {
 return (
 <button
 onClick={onClick}
 className="fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-brand text-white shadow-lg transition-transform hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-2"aria-label="Open help panel">
 <svg
 className="h-6 w-6"fill="none"viewBox="0 0 24 24"stroke="currentColor"strokeWidth={2}
 >
 <path
 strokeLinecap="round"strokeLinejoin="round"d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </button>
 );
}

/**
 * Help Panel - Slide-out panel with contextual help
 */
export function HelpPanel({
 isOpen,
 onClose,
}: {
 isOpen: boolean;
 onClose: () => void;
}) {
 const location = useLocation();
 const [activeTab, setActiveTab] = useState<"tips" | "glossary">("tips");

 // Get help content for current page
 const currentPath = location.pathname;
 const helpContent = PAGE_HELP[currentPath] || DEFAULT_HELP;

 // Get related glossary terms
 const relatedTerms = (
 (helpContent as (typeof PAGE_HELP)[string]).relatedTerms || []
 )
 .map((term: string) => ({ key: term, ...GLOSSARY[term] }))
 .filter((t: { key: string; term?: string }) => t.term);

 // Close on escape
 useEffect(() => {
 const handleEscape = (e: KeyboardEvent) => {
 if (e.key ==="Escape") onClose();
 };
 if (isOpen) {
 document.addEventListener("keydown", handleEscape);
 }
 return () => document.removeEventListener("keydown", handleEscape);
 }, [isOpen, onClose]);

 // Listen for custom event to open help
 useEffect(() => {
 const handleOpenHelp = () => {
 // This will be handled by parent component
 };
 window.addEventListener("openHelp", handleOpenHelp);
 return () => window.removeEventListener("openHelp", handleOpenHelp);
 }, []);

 if (!isOpen) return null;

 return (
 <>
 {/* Backdrop */}
 <div
 className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-sm transition-opacity"onClick={onClose}
 />

 {/* Panel */}
 <div className="fixed right-0 top-0 z-50 h-full w-full max-w-md transform overflow-hidden bg-white shadow-2xl transition-transform">
 {/* Header */}
 <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
 <div className="flex items-center gap-3">
 <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-brand-10 text-role-primary">
 <svg
 className="h-5 w-5"fill="none"viewBox="0 0 24 24"stroke="currentColor"strokeWidth={2}
 >
 <path
 strokeLinecap="round"strokeLinejoin="round"d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"/>
 </svg>
 </span>
 <div>
 <h2 className="text-lg font-semibold text-slate-900">
 {helpContent.title}
 </h2>
 <p className="text-xs text-slate-500">
 Page Help & Tips
 </p>
 </div>
 </div>
 <button
 onClick={onClose}
 className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"aria-label="Close help panel">
 <svg
 className="h-5 w-5"fill="none"viewBox="0 0 24 24"stroke="currentColor"strokeWidth={2}
 >
 <path
 strokeLinecap="round"strokeLinejoin="round"d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </button>
 </div>

 {/* Tabs */}
 <div className="flex border-b border-slate-200 px-6">
 <button
 onClick={() => setActiveTab("tips")}
 className={`border-b-2 px-4 py-3 text-sm font-medium transition ${
 activeTab ==="tips"?"border-role-primary text-role-primary":"border-transparent text-slate-500 hover:text-slate-700"}`}
 >
 Tips & Guide
 </button>
 <button
 onClick={() => setActiveTab("glossary")}
 className={`border-b-2 px-4 py-3 text-sm font-medium transition ${
 activeTab ==="glossary"?"border-role-primary text-role-primary":"border-transparent text-slate-500 hover:text-slate-700"}`}
 >
 Glossary
 </button>
 </div>

 {/* Content */}
 <div className="h-[calc(100%-140px)] overflow-y-auto p-6">
 {activeTab ==="tips"? (
 <div className="space-y-6">
 {/* Description */}
 <div className="rounded-xl bg-slate-50 p-4">
 <p className="text-sm text-slate-600">
 {helpContent.description}
 </p>
 </div>

 {/* Tips */}
 <div>
 <h3 className="mb-3 text-sm font-semibold text-slate-900">
 Quick Tips
 </h3>
 <ul className="space-y-3">
 {helpContent.tips.map((tip, index) => (
 <li
 key={index}
 className="flex items-start gap-3 text-sm text-slate-600">
 <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-role-primary">
 {index + 1}
 </span>
 {tip}
 </li>
 ))}
 </ul>
 </div>

 {/* Related terms */}
 {relatedTerms.length > 0 && (
 <div>
 <h3 className="mb-3 text-sm font-semibold text-slate-900">
 Key Terms
 </h3>
 <div className="space-y-2">
 {relatedTerms.map(
 (item: {
 key: string;
 term?: string;
 definition?: string;
 example?: string;
 }) => (
 <div
 key={item.key}
 className="rounded-lg border border-slate-200 p-3">
 <p className="text-sm font-medium text-slate-900">
 {item.term}
 </p>
 <p className="mt-1 text-xs text-slate-500">
 {item.definition}
 </p>
 </div>
 ),
 )}
 </div>
 </div>
 )}

 {/* Keyboard shortcuts */}
 <div>
 <h3 className="mb-3 text-sm font-semibold text-slate-900">
 Keyboard Shortcuts
 </h3>
 <div className="space-y-2">
 <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
 <span className="text-sm text-slate-600">
 Quick Search
 </span>
 <kbd className="rounded bg-slate-200 px-2 py-1 text-xs font-medium text-slate-700">
 Cmd+K
 </kbd>
 </div>
 <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
 <span className="text-sm text-slate-600">
 Close Panel
 </span>
 <kbd className="rounded bg-slate-200 px-2 py-1 text-xs font-medium text-slate-700">
 Esc
 </kbd>
 </div>
 </div>
 </div>
 </div>
 ) : (
 <div className="space-y-4">
 <p className="text-sm text-slate-500">
 Common terms and their meanings
 </p>

 {Object.entries(GLOSSARY).map(([key, entry]) => (
 <div
 key={key}
 className="rounded-lg border border-slate-200 p-3">
 <p className="text-sm font-medium text-slate-900">
 {entry.term}
 </p>
 <p className="mt-1 text-xs text-slate-500">
 {entry.definition}
 </p>
 {entry.example && (
 <p className="mt-2 text-xs italic text-slate-400">
 Example: {entry.example}
 </p>
 )}
 </div>
 ))}
 </div>
 )}
 </div>
 </div>
 </>
 );
}

/**
 * HelpProvider - Combines help button and panel
 * Add this to your app layout
 */
export function HelpProvider() {
 const [isOpen, setIsOpen] = useState(false);

 // Listen for custom event
 useEffect(() => {
 const handleOpenHelp = () => setIsOpen(true);
 window.addEventListener("openHelp", handleOpenHelp);
 return () => window.removeEventListener("openHelp", handleOpenHelp);
 }, []);

 return (
 <>
 <HelpButton onClick={() => setIsOpen(true)} />
 <HelpPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
 </>
 );
}

export default HelpProvider;

